import { Pause, PlayArrow } from "@material-ui/icons";
import { Slider } from "@material-ui/core";
import ReactPlayer from "react-player";
import { createRef, useState, useMemo, ChangeEvent } from "react";
import "./ReactAudioPlayer.css";
import handleDuration from "../../../../services/recorder/HandleDuration";

interface ReadleeAudioPlayerProps {
  audioUrls: string[];
  audioDurations: number[];
  onDuration?: (dur: number) => void;
  narrow?: boolean;
}

const getTotalDuration = (audioDurations: number[]): number =>
  audioDurations.reduce((dur: number, sum: number): number => dur + sum, 0);

const ReadleeAudioPlayer = (props: ReadleeAudioPlayerProps) => {
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudioTime, setCurrentAudioTime] = useState(0);
  const audioPlayerRef = createRef<ReactPlayer>();

  const totalDuration = useMemo<number>(() => getTotalDuration(props.audioDurations), [props.audioDurations]);
  const cumulativeDurations = useMemo<Array<number>>(
    () => props.audioDurations.map((_, idx) => getTotalDuration(props.audioDurations.slice(0, idx))),
    [props.audioDurations],
  );

  const onProgress = ({ playedSeconds, played }: { playedSeconds: number; played: number }) => {
    const duration = (cumulativeDurations[currentAudioIndex] || 0) + playedSeconds;
    setCurrentAudioTime(currentAudioIndex === props.audioDurations.length - 1 && played === 1 ? 0 : duration);
  };

  const onSeek = (_ev: ChangeEvent<{}>, value: number | Array<number>) => {
    if (typeof value === "number") {
      const { time, idx } = props.audioDurations.reduce(
        (acc, cur, idx) => {
          const runningTotal = cumulativeDurations[idx] || 0;
          return value - runningTotal > cur ? { idx: idx + 1, time: value - cur - runningTotal } : acc;
        },
        { idx: 0, time: value },
      );
      if (audioPlayerRef.current) {
        audioPlayerRef.current.seekTo(time, "seconds");
        setCurrentAudioIndex(idx);
        setCurrentAudioTime(value);
      }
    }
  };

  const grabNextUrl = () => {
    if (currentAudioIndex === props.audioUrls.length - 1) {
      setCurrentAudioIndex(0);
      setCurrentAudioTime(0);
      setIsPlaying(false);
    } else {
      setCurrentAudioIndex(currentAudioIndex + 1);
    }
    audioPlayerRef.current?.seekTo(0);
  };

  return (
    <div className={`readleeAudioPlayer${props.narrow ? " narrow" : ""}`}>
      {!isPlaying ? <PlayArrow onClick={() => setIsPlaying(true)} /> : <Pause onClick={() => setIsPlaying(false)} />}
      <div className="component">{handleDuration(currentAudioTime)}</div>
      <Slider className="component" value={currentAudioTime} min={0} max={totalDuration} onChange={onSeek} step={0.1} />
      <div className="component">{handleDuration(totalDuration)}</div>
      <ReactPlayer
        url={props.audioUrls[currentAudioIndex]}
        playing={isPlaying}
        width={0}
        height={0}
        onEnded={grabNextUrl}
        ref={audioPlayerRef}
        onProgress={onProgress}
        onDuration={props.onDuration}
        progressInterval={500}
        config={{ file: { attributes: { controlsList: ["nodownload"] } } }}
      />
    </div>
  );
};

export default ReadleeAudioPlayer;
